// Site SSE stream — per-user notifications (game invitations, friend
// state changes, changelog announcements, etc.).
//
// One persistent EventSource per signed-in user, shared across components.
// Components call subscribe(type, handler) and receive a fire-and-forget
// poke whenever the server emits that event type; the handler is
// responsible for refetching authoritative data via REST.
//
// Usage:
//   import { siteEvents } from '../lib/site-events.js';
//   const unsubscribe = siteEvents.subscribe('game-invited', () => {...});

import { getBackendUrl } from './api.js';
import { authState } from './auth.js';

/**
 * @typedef {{ type: string }} SiteEvent
 */

class SiteEventsClient {
  constructor() {
    /** @type {EventSource|null} */
    this._source = null;
    /** @type {Map<string, Set<(event: SiteEvent) => void>>} */
    this._handlers = new Map();
    this._connecting = false;
  }

  /**
   * Subscribe to a site-event type. Returns an unsubscribe fn.
   * @param {string} type
   * @param {(event: SiteEvent) => void} handler
   * @returns {() => void}
   */
  subscribe(type, handler) {
    if (!this._handlers.has(type)) {
      this._handlers.set(type, new Set());
    }
    const set = /** @type {Set<(event: SiteEvent) => void>} */ (this._handlers.get(type));
    set.add(handler);
    this._ensureConnected();
    return () => {
      set.delete(handler);
      if (set.size === 0) this._handlers.delete(type);
      if (this._handlers.size === 0) this._disconnect();
    };
  }

  async _ensureConnected() {
    if (this._source || this._connecting) return;
    this._connecting = true;
    try {
      const token = await authState.getAccessToken();
      if (!token) {
        this._connecting = false;
        return;
      }
      const url = `${getBackendUrl()}/api/me/events?token=${encodeURIComponent(token)}`;
      const source = new EventSource(url);
      source.onmessage = (e) => this._dispatch(e.data);
      source.onerror = (err) => {
        console.warn('[site-events] stream error, browser will auto-reconnect', err);
      };
      this._source = source;
    } finally {
      this._connecting = false;
    }
  }

  _disconnect() {
    if (this._source) {
      this._source.close();
      this._source = null;
    }
  }

  /** @param {string} data */
  _dispatch(data) {
    let payload;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    if (!payload || typeof payload.type !== 'string') return;
    const set = this._handlers.get(payload.type);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(payload);
      } catch (err) {
        console.error('[site-events] handler threw', err);
      }
    }
  }
}

export const siteEvents = new SiteEventsClient();
